import {
    ProgressCircle,
    type ProgressCircleShape,
    type ProgressCircleSize,
} from '@trustoo/components/ProgressCircle/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const PROGRESS_CIRCLE_USAGE = `import { ProgressCircle } from '@trustoo/components/ProgressCircle/v2'

// Default md circle with value
<ProgressCircle value={40} aria-label="Active users" />

// With a caption label
<ProgressCircle value={40} label="Active users" />

// Half-circle gauge
<ProgressCircle value={40} shape="half-circle" label="Active users" />

// Sizes
<ProgressCircle value={40} size="xs" />
<ProgressCircle value={40} size="lg" />

// Custom center content
<ProgressCircle value={72}>
  <span className="text-display-sm font-semibold text-text-primary">72</span>
</ProgressCircle>`

export const ProgressCircleShowcase: React.FC = () => (
    <Section
        title="ProgressCircle (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Progress circle</span> set
                (ComponentSet <InlineCode>5:18023</InlineCode>). A determinate ring / gauge drawn as
                an inline SVG (<InlineCode>stroke-background-bg-quaternary</InlineCode> track +{' '}
                <InlineCode>stroke-background-bg-brand-solid</InlineCode> arc, round caps). Import
                v2 explicitly via <InlineCode>@trustoo/components/ProgressCircle/v2</InlineCode>{' '}
                (net-new — no v1 barrel yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes (circle)</h3>
                <div className="flex flex-wrap items-end gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['xxs', 'xs', 'sm', 'md', 'lg'] as ProgressCircleSize[]).map((size) => (
                        <div key={size} className="flex flex-col items-center gap-spacing-md">
                            <ProgressCircle
                                value={40}
                                size={size}
                                label="Users"
                                aria-label={`${size} 40 percent`}
                            />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {size}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Shapes</h3>
                <div className="flex flex-wrap items-end gap-spacing-5xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['circle', 'half-circle'] as ProgressCircleShape[]).map((shape) => (
                        <div key={shape} className="flex flex-col items-center gap-spacing-md">
                            <ProgressCircle
                                value={40}
                                size="md"
                                shape={shape}
                                label="Active users"
                                aria-label={`${shape} 40 percent`}
                            />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {shape}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Progress values</h3>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {[0, 25, 50, 75, 100].map((value) => (
                        <ProgressCircle
                            key={value}
                            value={value}
                            size="xs"
                            aria-label={`${value} percent`}
                        />
                    ))}
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={PROGRESS_CIRCLE_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>value</InlineCode> + <InlineCode>min</InlineCode> /{' '}
                    <InlineCode>max</InlineCode>, <InlineCode>size</InlineCode> (xxs · xs · sm · md
                    · lg), <InlineCode>shape</InlineCode> (circle · half-circle),{' '}
                    <InlineCode>label</InlineCode>, <InlineCode>showValue</InlineCode>,{' '}
                    <InlineCode>formatValue</InlineCode>, and a <InlineCode>children</InlineCode>{' '}
                    override for custom center content. Renders a{' '}
                    <InlineCode>role=&quot;progressbar&quot;</InlineCode>; the ring is an inline
                    SVG.
                </p>
            </div>
        </div>
    </Section>
)
